define(
  'galleryModalHandler',[
    'builderShowcaseObserver',
    'builderShowcaseGalleryStateHandler',
    'routes',
    'modalBackground',
  ],
  (
    builderShowcaseObserver,
    galleryStateHandler,
    routes
  ) => {
    'use strict';

    /**
    * @private
    * buildImagesCollectionFromImageTag: builds objects after interpreting img tags.
    * These objects are the ones interpreted by internal modules to build elements like
    * filmstrips, arrows, main viewers, etc.
    * @param {Array} images collection with a set of img tags from DOM.
    *
    */
    function buildImagesCollectionFromImageTag(images) {
      const imagesArray = [];
      for (let i = 0; i < images.length; i += 1) {
        const image = {
          Id: i,
          Thumbnail: images[i].src,
          isLiteGalleryModal: true,
          MainImageSources: [],
          MainPictureImage: {
            ImageUrl: images[i].src,
            NoPhoto: '',
          }
        };

        imagesArray.push(image);
      }

      return imagesArray;
    }

    /**
    * @private
    * openModalFromImagesArray: takes the information needed to open up a modal from the
     * information set in the HTML element for an img tag.
    * @param {HTML} selectedItem the user is interested in view on the main viewer.
    *
    */
    function openModalFromImagesArray(imageToOpen) {
      require(
        ['galleryModalOpenerSubPage'],
        (galleryModalOpenerSubPage) => {
          window.NewHomeSource.ModalBackground.createBackground('rgba(0,0,0,.82)');

          const imagesContainer = imageToOpen.closest('[data-reviews-images]');

          if (!imagesContainer) {
            return;
          }

          const images = imagesContainer.querySelectorAll('img');
          const imagesArray = buildImagesCollectionFromImageTag(images);
          const clickedItem = imagesArray.find((element) => element.Thumbnail === imageToOpen.src);

          const dataForSubscribers = {
            collectionImages: { },
            selectedItem: clickedItem,
            isLiteGalleryModal: true,
          };

          dataForSubscribers.collectionImages.Items = imagesArray;

          galleryModalOpenerSubPage.init(builderShowcaseObserver.galleryOpened.bind(this, dataForSubscribers));
        }
      );
    }

    /**
    * @private
    * openModalFireEvent: fires the galleryOpened event for the subscribers
    * @param {HTML} selectedItem the user is interested in view on the main viewer.
    *
    */
    function openModalFireEvent(selectedItem) {
      require(
        ['galleryModalOpener'],
        () => {
          window.NewHomeSource.ModalBackground.createBackground('rgba(0,0,0,.82)');
          const images = galleryStateHandler.getCollection(selectedItem.dataset.galleryCollection);
          const galleryItemId = parseInt(selectedItem.dataset.galleryItemId, 10);
          const clickedItem = images.Items.find((element) => element.Id === galleryItemId);
          const dataForSubscribers = {
            collectionImages: images,
            selectedItem: clickedItem,
            isLiteGalleryModal: false,
          };

          builderShowcaseObserver.galleryOpened(dataForSubscribers);
        }
      );
    }

    /**
    * @private
    * prepareImagesFromAjax: when the images comes from an AJAX request, this method will prepare an array with
     * this images ready to be used as a filmstrip on the modal.
    * @param {Array} collections of images to be shown in the modal filmstrip.
    * @param {int} imageId representing the id of the selected image.
    *
    */
    function prepareImagesFromAjax(collections, imageId) {
      const images = [];
      const mainCollection = collections.splice(collections.findIndex((item) => item.IsMainCollection), 1)[0].Items;
      const selectedImage = mainCollection.splice(mainCollection.findIndex((item) => item.Id === imageId), 1);

      mainCollection.unshift(selectedImage[0]);
      images.push(...mainCollection);
      for (let i = 0; i < collections.length; i += 1) {
        if (!collections[i].IsMainCollection && collections[i].Items != null) {
          images.push(...collections[i].Items);
        }
      }

      return {
        Items: images,
      };
    }

    /**
    * @private
    * openModalGallerySubPage: function to be executed when the images to show in the modal comes
     * from an AJAX request.
    * @param {HTML} clickedItem the user is interested in view on the main viewer.
    *
    */
    function openModalGallerySubPage(clickedItem) {
      window.NewHomeSource.ModalBackground.createBackground('rgba(0,0,0,.82)');

      const data = builderShowcaseObserver.getState().data;
      let metroAreasCount = data.metroAreasCount || 0;
      const hasConsultedAboutReviews = data.hasConsultedAboutReviews || false;
      const imageId = clickedItem.dataset.galleryItemId;
      const imageCategory = clickedItem.dataset.galleryCollection;
      const planId = clickedItem.dataset.planId;
      const brandId = data.GalleryViewModel.BrandId;
      const brandName = data.GalleryViewModel.BrandName;
      const hasBuilderReviews = data.hasBuilderReviews;
      const hasPlanLibrary = data.hasPlanLibrary;
      const url = routes.getGalleryInformationBasedOnPlan;
      const plan = `planid=${planId}`;
      const image = `imageId=${imageId}`;
      const category = `imageCategory=${imageCategory}`;
      const brand = `brandId=${brandId}`;
      const name = `brandName=${brandName}`;
      const metro = `MetroAreasCount=${metroAreasCount}`;
      const hasReviews = `hasBuilderReviews=${hasBuilderReviews}`;
      const hasPlanLibraryHomes = `HasPlanLibraryHomes=${hasPlanLibrary}`;
      const reviewsCheck = `HasConsultedAboutReviews=${hasConsultedAboutReviews}`;
      // eslint-disable-next-line max-len
      const urlForPlan = `${url}?${plan}&${image}&${category}&${brand}&${name}&${hasReviews}&${metro}&${reviewsCheck}&${hasPlanLibraryHomes}`;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', urlForPlan, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send();

      xhr.onreadystatechange = function () {
        if (xhr.readyState === 4) {
          const jsonResponse = JSON.parse(xhr.response);
          require(
            ['galleryModalOpenerSubPage'],
            (galleryModalOpenerSubPage) => {
              const imageIdAsInt = parseInt(imageId, 10);
              const collectionsCopy = Object.assign([], jsonResponse.Collections);
              const collectionImages = prepareImagesFromAjax(collectionsCopy, imageIdAsInt);
              const selectedItem = collectionImages.Items.find((element) => element.Id === imageIdAsInt);
              const dataForSubscribers = {
                collectionImages,
                selectedItem,
                jsonResponse,
                isGallerySubPage: true,
                isLiteGalleryModal: false,
                data,
              };

              galleryModalOpenerSubPage.init(builderShowcaseObserver.galleryOpened.bind(this, dataForSubscribers));
            }
          );

          if (metroAreasCount === 0 || imageCategory === 'Videos') {
            metroAreasCount = jsonResponse.MetroAreasCount || 0;
            builderShowcaseObserver.setData({
              metroAreasCount,
            });

            builderShowcaseObserver.commit();
          }

          if (data.hasConsultedAboutReviews === undefined) {
            builderShowcaseObserver.setData({
              hasConsultedAboutReviews: jsonResponse.HasConsultedAboutReviews,
              hasBuilderReviews: jsonResponse.HasBuilderReviews,
              averageRating: jsonResponse.RatingsAverageScore
            });
          }

          builderShowcaseObserver.setData({
            hasPlanLibrary: jsonResponse.HasPlanLibraryHomes || false,
          });

          builderShowcaseObserver.commit();
        }
      };
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!event.target) {
          return;
        }

        if (event.target.dataset && event.target.dataset.role === 'review-image') {
          openModalFromImagesArray(event.target);
          return;
        }

        const figure = event.target.closest('figure');

        if (!figure) {
          return;
        }

        const target = figure.querySelector('figcaption');
        if (target && target.dataset.galleryItemId !== undefined) {
          if (document.querySelector('[data-gallery-sub-page-container]')) {
            openModalGallerySubPage(target);
            return;
          }

          openModalFireEvent(target);
        }
      }
    );
  }
);

